import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  useDisclosure,
  IconButton,
  Heading,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Badge,
  Text,
  VStack,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Icon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Select,
  UnorderedList,
  ListItem,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { FaEdit, FaTrash, FaHistory, FaSearch, FaSort, FaSortUp, FaSortDown, FaPlus, FaQuestionCircle, FaTimes, FaEnvelope, FaComments } from 'react-icons/fa';
import { MdMessage } from 'react-icons/md';
import { QuestionIcon } from '@chakra-ui/icons';
import MemberModal from './MemberModal';
import HelpModal from './HelpModal';
import { MessageHistoryModal } from './MessageHistory';
import EmailAuditLog from './EmailAuditLog';
import { useState, useMemo, useEffect, useRef } from 'react';
import { formatPhoneNumber } from '../utils/phoneUtils';
import { supabase } from '../supabaseClient';
import { sendWelcomeEmail } from '../utils/emailService';
import { useNavigate } from 'react-router-dom';
import { useMenuTitle } from '../hooks/useMenuTitle';

const MemberList = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const messageHistoryModal = useDisclosure();
  const emailHistoryModal = useDisclosure();
  const [selectedMember, setSelectedMember] = useState(null);
  const [messageHistory, setMessageHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('full_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [members, setMembers] = useState([]);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 50;
  const cancelRef = useRef();
  const toast = useToast();
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const navigate = useNavigate();
  const menuTitle = useMenuTitle();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('texting_contacts')
        .select('*')
        .order(sortField, { ascending: sortDirection === 'asc' })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (error) throw error;
      
      // Check if we have more pages
      setHasMore(data?.length === PAGE_SIZE);
      
      if (page === 0) {
        setMembers(data || []);
      } else {
        setMembers(prev => [...prev, ...(data || [])]);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
      toast({
        title: 'Error fetching members',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(0);
    fetchMembers();
  }, [sortField, sortDirection, statusFilter]);

  useEffect(() => {
    if (page > 0) {
      fetchMembers();
    }
  }, [page]);

  const loadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  // Add intersection observer for infinite scroll
  const observerTarget = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loading]);

  const onDeleteMember = async () => {
    if (!memberToDelete) return;

    try {
      const user = await supabase.auth.getUser();
      
      // First create the audit log entry
      const { error: auditError } = await supabase
        .from('contact_audit_log')
        .insert({
          contact_id: memberToDelete.id,
          action: 'DELETE',
          old_data: memberToDelete,
          new_data: null,
          changed_by: user.data.user?.id,
          changed_at: new Date().toISOString()
        });

      if (auditError) throw auditError;

      // Delete the contact - related records will be deleted via ON DELETE CASCADE
      const { error } = await supabase
        .from('texting_contacts')
        .delete()
        .eq('id', memberToDelete.id);

      if (error) {
        console.error('Delete error:', error);
        throw new Error('Failed to delete contact. This may be because there are related records that need to be deleted first.');
      }

      toast({
        title: 'Contact deleted',
        description: `${memberToDelete.first_name} ${memberToDelete.last_name} has been removed along with all related records.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      fetchMembers(); // Refresh the list
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete contact. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleteAlertOpen(false);
      setMemberToDelete(null);
    }
  };

  const getConsentStatus = (member) => {
    if (!member.consent_status) return 'PENDING';
    return member.consent_status;
  };

  const getConsentBadgeProps = (status) => {
    const props = {
      PENDING: { colorScheme: 'yellow', children: 'Pending' },
      CONSENTED: { colorScheme: 'green', children: 'Consented' },
      REVOKED: { colorScheme: 'red', children: 'Revoked' },
    };
    return props[status] || props.PENDING;
  };

  // Sorting functionality:
  // 1. Each column (except Actions) can be sorted by clicking the column header
  // 2. First click sorts ascending, second click sorts descending
  // 3. Sort icons indicate:
  //    - Gray double arrow (↕): Column is sortable but not currently sorted
  //    - Black up arrow (↑): Column is sorted ascending
  //    - Black down arrow (↓): Column is sorted descending
  const handleSort = (field) => {
    if (sortField === field) {
      // If clicking the same field, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new field, set it as the sort field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort and filter the member list based on current sort field, direction, and filters
  const filteredAndSortedMembers = useMemo(() => {
    let result = [...members];
    
    // Apply search filter across name, email, and phone
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(member =>
        member.full_name?.toLowerCase().includes(searchLower) ||
        member.email?.toLowerCase().includes(searchLower) ||
        member.phone_number?.includes(searchTerm)
      );
    }
    
    // Apply status filter if not showing all
    if (statusFilter !== 'all') {
      result = result.filter(member => getConsentStatus(member) === statusFilter);
    }
    
    // Sort the filtered results
    result.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      // Handle special cases for formatted fields
      if (sortField === 'phone_number') {
        // Strip formatting for phone number comparison
        aValue = aValue?.replace(/\D/g, '');
        bValue = bValue?.replace(/\D/g, '');
      }
      
      // Handle null/undefined values
      if (!aValue) return 1;
      if (!bValue) return -1;
      
      // Compare values based on sort direction
      const comparison = aValue.localeCompare(bValue);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return result;
  }, [members, searchTerm, statusFilter, sortField, sortDirection]);

  const getSortIcon = (field) => {
    if (sortField === field) {
      return <Icon as={sortDirection === 'asc' ? FaSortUp : FaSortDown} />;
    }
    return <Icon as={FaSort} color="gray.300" />;
  };

  const handleEdit = (member) => {
    setSelectedMember(member);
    onOpen();
  };

  const handleSave = async (memberData) => {
    try {
      if (!memberData || !memberData.first_name || !memberData.last_name) {
        throw new Error('First name and last name are required');
      }

      console.log('Member data received:', memberData);

      // For new members, we'll get the data back from the modal's insert
      // For existing members, we'll get the updated data
      const savedData = memberData;

      // Update the members list
      setMembers(prev => {
        const index = prev.findIndex(m => m.id === savedData.id);
        if (index >= 0) {
          // Update existing member
          const updated = [...prev];
          updated[index] = savedData;
          return updated;
        } else {
          // Add new member
          return [...prev, savedData];
        }
      });

      // Log the update in audit log
      if (savedData.id) {
        const user = await supabase.auth.getUser();
        const userId = user.data.user.id;
        
        const { error: auditError } = await supabase
          .from('contact_audit_log')
          .insert({
            action: selectedMember ? 'UPDATE' : 'INSERT',
            contact_id: savedData.id,
            changed_by: userId,
            changes: {
              first_name: savedData.first_name,
              last_name: savedData.last_name,
              phone_number: savedData.phone_number,
              email: savedData.email
            }
          });

        if (auditError) {
          console.error('Failed to log audit:', auditError);
        }
      }

      toast({
        title: selectedMember ? 'Contact Updated' : 'Contact Created',
        description: `Successfully ${selectedMember ? 'updated' : 'created'} contact`,
        status: 'success',
        duration: 3000,
      });

    } catch (error) {
      console.error('Error saving member:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleAdd = () => {
    setSelectedMember(null);
    onOpen();
  };

  const openMessageHistory = (member) => {
    setSelectedMember(member);
    messageHistoryModal.onOpen();
  };

  const handleDelete = (member) => {
    setMemberToDelete(member);
    setIsDeleteAlertOpen(true);
  };

  // Update the help content to reflect the new interface
  const contactsHelpContent = (
    <VStack align="start" spacing={4}>
      <Box>
        <Text fontWeight="bold" mb={2}>Search and Filtering</Text>
        <UnorderedList>
          <ListItem>Use the search bar to filter contacts by name, email, or phone number</ListItem>
          <ListItem>Click the "x" in the search bar to clear your search</ListItem>
          <ListItem>Use the status dropdown to filter by consent status</ListItem>
        </UnorderedList>
      </Box>

      <Box>
        <Text fontWeight="bold" mb={2}>Sorting</Text>
        <UnorderedList>
          <ListItem>Click any column header to sort by that field</ListItem>
          <ListItem>Sort icons indicate:
            <UnorderedList>
              <ListItem>Gray double arrow (↕): Column is sortable</ListItem>
              <ListItem>Black up arrow (↑): Sorted ascending (A to Z)</ListItem>
              <ListItem>Black down arrow (↓): Sorted descending (Z to A)</ListItem>
            </UnorderedList>
          </ListItem>
        </UnorderedList>
      </Box>

      <Box>
        <Text fontWeight="bold" mb={2}>Actions</Text>
        <UnorderedList>
          <ListItem>+ : Add a new contact</ListItem>
          <ListItem>Message (chat): View message history</ListItem>
          <ListItem>Email (envelope): View email history</ListItem>
          <ListItem>Edit (pencil): Modify contact details</ListItem>
          <ListItem>Delete (trash): Remove a contact</ListItem>
        </UnorderedList>
      </Box>

      <Box>
        <Text fontWeight="bold" mb={2}>Consent Status Colors</Text>
        <UnorderedList>
          <ListItem>Green: Active consent</ListItem>
          <ListItem>Yellow: Pending consent</ListItem>
          <ListItem>Red: Revoked consent</ListItem>
          <ListItem>Orange: Consented (legacy)</ListItem>
        </UnorderedList>
      </Box>
    </VStack>
  );

  return (
    <Box p={4} h="calc(100vh - 100px)" display="flex" flexDirection="column">
      <HStack justify="space-between" mb={4}>
        <Heading size="lg">{menuTitle}</Heading>
        <HStack>
          <IconButton
            icon={<FaPlus />}
            aria-label="Add contact"
            size="sm"
            colorScheme="blue"
            onClick={handleAdd}
          />
          <IconButton
            icon={<QuestionIcon />}
            aria-label="Help"
            size="sm"
            variant="ghost"
            onClick={() => setIsHelpOpen(true)}
          />
        </HStack>
      </HStack>

      <Flex gap={2} mb={2}>
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none">
            <Icon as={FaSearch} color="gray.300" />
          </InputLeftElement>
          <Input
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="sm"
          />
          {searchTerm && (
            <InputRightElement>
              <IconButton
                icon={<FaTimes />}
                aria-label="Clear search"
                size="xs"
                variant="ghost"
                onClick={() => setSearchTerm('')}
              />
            </InputRightElement>
          )}
        </InputGroup>

        <Select
          w="200px"
          size="sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="CONSENTED">Consented</option>
          <option value="PENDING">Pending</option>
          <option value="REVOKED">Revoked</option>
        </Select>
      </Flex>

      <Box overflowY="auto" flex={1} mx={-4} px={4}>
        <Table variant="simple" size="xs">
          <Thead position="sticky" top={0} bg="white" zIndex={1}>
            <Tr>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('full_name')}>
                <HStack spacing={1} display="inline-flex" alignItems="center">
                  <Text>NAME</Text>
                  {getSortIcon('full_name')}
                </HStack>
              </Th>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('email')}>
                <HStack spacing={1} display="inline-flex" alignItems="center">
                  <Text>EMAIL</Text>
                  {getSortIcon('email')}
                </HStack>
              </Th>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('phone_number')}>
                <HStack spacing={1} display="inline-flex" alignItems="center">
                  <Text>PHONE</Text>
                  {getSortIcon('phone_number')}
                </HStack>
              </Th>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('consent_status')}>
                <HStack spacing={1} display="inline-flex" alignItems="center">
                  <Text>CONSENT STATUS</Text>
                  {getSortIcon('consent_status')}
                </HStack>
              </Th>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal" textAlign="right">ACTIONS</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredAndSortedMembers.map((member) => (
              <Tr key={member.id}>
                <Td py={0} px={1} fontSize="xs">{member.full_name}</Td>
                <Td py={0} px={1} fontSize="xs">{member.email}</Td>
                <Td py={0} px={1} fontSize="xs">{formatPhoneNumber(member.phone_number)}</Td>
                <Td py={0} px={1} fontSize="xs">
                  <Badge {...getConsentBadgeProps(getConsentStatus(member))} fontSize="2xs" />
                </Td>
                <Td py={0} px={1} fontSize="xs">
                  <HStack spacing={1} justify="flex-end">
                    <IconButton
                      icon={<FaComments />}
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      aria-label="View Message History"
                      title="View message history"
                      onClick={() => openMessageHistory(member)}
                    />
                    <IconButton
                      icon={<FaEnvelope />}
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      aria-label="View Email History"
                      title="View email history"
                      onClick={() => {
                        setSelectedMember(member);
                        emailHistoryModal.onOpen();
                      }}
                    />
                    <IconButton
                      icon={<FaEdit />}
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      aria-label="Edit Member"
                      onClick={() => handleEdit(member)}
                    />
                    <IconButton
                      icon={<FaTrash />}
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      aria-label="Delete Member"
                      onClick={() => handleDelete(member)}
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {loading && (
          <Center py={4}>
            <Spinner size="sm" />
          </Center>
        )}
        <Box ref={observerTarget} h="20px" />
      </Box>

      <MemberModal
        isOpen={isOpen}
        onClose={onClose}
        member={selectedMember}
        onSave={handleSave}
      />

      <MessageHistoryModal
        isOpen={messageHistoryModal.isOpen}
        onClose={messageHistoryModal.onClose}
        recipientId={selectedMember?.id}
        recipientName={selectedMember ? `${selectedMember.first_name} ${selectedMember.last_name}` : ''}
      />

      <Modal
        isOpen={emailHistoryModal.isOpen}
        onClose={emailHistoryModal.onClose}
        size="6xl"
      >
        <ModalOverlay />
        <ModalContent maxW="90vw" maxH="90vh" h="90vh">
          <ModalHeader pb={2}>Email History - {selectedMember?.first_name} {selectedMember?.last_name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody p={0}>
            <EmailAuditLog recipientId={selectedMember?.id} isModal={true} />
          </ModalBody>
        </ModalContent>
      </Modal>

      <HelpModal
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title="Members Help"
        content={contactsHelpContent}
      />

      <AlertDialog
        isOpen={isDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsDeleteAlertOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Contact
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete the contact record for {memberToDelete?.first_name} {memberToDelete?.last_name} and all their related records? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsDeleteAlertOpen(false)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={onDeleteMember} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default MemberList;
